import ExclamationTriangleIcon from '@heroicons/react/24/outline/ExclamationTriangleIcon';
import PencilIcon from '@heroicons/react/24/outline/PencilIcon';
import { isEmpty, isEqual } from 'radash';
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/core/Accordion';
import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import { Icon } from '@/components/core/Icon';
import LabelValue from '@/components/core/LabelValue/LabelValue';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { AttributeEditPermissionProvider } from '@/components/datamodel/hooks/useIsAttributeReadonly';
import { ResolvedLayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useValidationTranslator } from '@/components/datamodel/hooks/useValidationTranslator';
import type { BaseValidation } from '@/components/datamodel/types';
import { DiscardChangesConfirmationModal } from '@/components/DiscardChangesConfirmationModal';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetApiRecord } from '@/pages/dam/api/assets.types';
import { type DamDetailGroup, isDamSystemAttribute } from '@/pages/dam/utils/damDetailSections';
import {
    resolveDamAttributeStates,
    resolveDamValidationMessages,
    toAttributeStateLists,
    toInitialValidations,
} from '@/pages/dam/utils/damValidationMessages';
import { useDamLiveValidation, type UseDamLiveValidationOptions } from '@/pages/dam/utils/useDamLiveValidation';
import type { DataModelAttribute, DataModelLayout } from '@/types/DataModel';

import { type DamDetailRowActions, useDamDetailRows } from './useDamDetailRows';

interface DamAssetDataPanelProps extends DamDetailRowActions {
    asset: AssetApiRecord;
    /** Attributes of the `ASSET_DATA` section, merged with their definition. */
    attributes: DataModelAttribute[];
    /** Further sections of `asset_detail`, shown as collapsible groups below the primary rows (D-056). */
    groups?: DamDetailGroup[];
    /** `asset_detail`, for the data-model editor. */
    layout: DataModelLayout;
    /** Names of the assigned tags. */
    tags: string[];
    /** Height to collapse the read rows to ("Show N more"); `undefined` shows all rows. */
    maxHeight?: number;
    /** Persists the changed business values; rejecting keeps the panel in edit mode. */
    onSave: (changed: Record<string, unknown>) => Promise<unknown>;
    onManageTags: () => void;
    /** Whether the editor holds values that differ from the asset. */
    onUnsavedChange?: (hasUnsavedChanges: boolean) => void;
}

/** Editors report absent values as `''` or `[]`; those are not changes. */
const normalizeBlank = (value: unknown) =>
    (value === undefined || value === null || (typeof value !== 'number' && typeof value !== 'boolean' && isEmpty(value)) ? null : value);

const divider = 'tw-m-0 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-divider';

const groupTriggerClassName = 'tw-py-3 tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base';

const hasValue = (value: unknown) =>
    value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);

/** Translated like the sections of the item editor: `section.<name>`, else the layout label, else `fallback` or the name. */
const useSectionTitle = () => {
    const { t } = useTranslation();
    return useCallback((section: Pick<DamDetailGroup, 'name' | 'label'>, fallback?: string) => {
        const key = `section.${section.name}`;
        const translated = t(key);
        return translated === key ? (section.label || fallback || section.name) : translated;
    }, [t]);
};

type DamDetailRows = ReturnType<typeof useDamDetailRows>;

/** Splits rows built for the attributes of all groups back into one list per group. */
const splitRows = (groups: DamDetailGroup[], rows: DamDetailRows): DamDetailRows[] => {
    let offset = 0;
    return groups.map((group) => {
        const groupRows = rows.slice(offset, offset + group.attributes.length);
        offset += group.attributes.length;
        return groupRows;
    });
};

/**
 * "Asset data" of the detail page (wireframe `7014:143575`): rows from `ASSET_DATA` with
 * "Show N more" while they exceed `maxHeight`, further `asset_detail` sections as collapsible
 * groups (the first open, D-056), Edit with the data-model editor for business attributes of
 * all sections (system values stay read-only), and the tags with "Manage" (D-043).
 */
function DamAssetDataPanel({
    asset,
    attributes,
    groups = [],
    layout,
    tags,
    maxHeight,
    onSave,
    onManageTags,
    onUnsavedChange,
    onShowLinkedArticles,
    onShowHistory,
}: DamAssetDataPanelProps) {
    const { t } = useTranslation();
    const { translateValidationLabel, translateValidationMessage } = useValidationTranslator();
    const formId = useId();
    const [isEditing, setIsEditing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasSaveError, setHasSaveError] = useState(false);
    const [hasEditorChanges, setHasEditorChanges] = useState(false);
    const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
    /** `null` until the user opens or closes a group: then the first shown group is open. */
    const [openGroups, setOpenGroups] = useState<string[] | null>(null);
    const sectionTitle = useSectionTitle();
    const hasUnsavedChanges = isEditing && hasEditorChanges;

    useEffect(() => {
        onUnsavedChange?.(hasUnsavedChanges);
    }, [hasUnsavedChanges, onUnsavedChange]);

    const stopEditing = () => {
        setHasSaveError(false);
        setHasEditorChanges(false);
        setIsConfirmingCancel(false);
        setIsEditing(false);
    };
    const [visibleCount, setVisibleCount] = useState<number | undefined>(undefined);
    const panelRef = useRef<HTMLElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const groupsRef = useRef<HTMLDivElement>(null);

    const states = useMemo(() => resolveDamAttributeStates(asset), [asset]);
    const messages = useMemo(
        () => resolveDamValidationMessages(asset, translateValidationLabel, translateValidationMessage),
        [asset, translateValidationLabel, translateValidationMessage],
    );
    const shownAttributes = useMemo(
        () => attributes.filter((attribute) => !states[attribute.name]?.has('hidden')),
        [attributes, states],
    );
    const messagesOf = useCallback((attribute: string) =>
        [...(messages.errors[attribute] ?? []), ...(messages.warnings[attribute] ?? [])], [messages]);

    /** Read mode leaves out groups without a value unless they carry a message (D-056). */
    const shownGroups = useMemo(
        () => groups
            .map((group) => ({ ...group, attributes: group.attributes.filter((attribute) => !states[attribute.name]?.has('hidden')) }))
            .filter((group) => group.attributes.some((attribute) => hasValue(asset[attribute.name]) || messagesOf(attribute.name).length > 0)),
        [groups, states, asset, messagesOf],
    );
    const openGroupNames = openGroups ?? (shownGroups[0] ? [shownGroups[0].name] : []);
    const rows = useDamDetailRows(asset, shownAttributes, { onShowLinkedArticles, onShowHistory });
    const editorRows = useDamDetailRows(asset, attributes, { onShowLinkedArticles, onShowHistory });
    const groupRows = splitRows(
        shownGroups,
        useDamDetailRows(asset, shownGroups.flatMap((group) => group.attributes), { onShowLinkedArticles, onShowHistory }),
    );
    const editorGroupRows = splitRows(
        groups,
        useDamDetailRows(asset, groups.flatMap((group) => group.attributes), { onShowLinkedArticles, onShowHistory }),
    );

    const canCollapse = maxHeight !== undefined && !isEditing && !isExpanded;

    useLayoutEffect(() => {
        const panel = panelRef.current;
        const list = listRef.current;
        if (!canCollapse || !panel || !list) {
            setVisibleCount(undefined);
            return;
        }
        const rowHeight = list.firstElementChild?.getBoundingClientRect().height ?? 0;
        if (rowHeight <= 0) {
            return;
        }
        const groupsHeight = groupsRef.current?.getBoundingClientRect().height ?? 0;
        const fixedHeight = panel.getBoundingClientRect().height - list.getBoundingClientRect().height - groupsHeight;
        const available = maxHeight - fixedHeight;
        const fitting = Math.floor(available / rowHeight);
        setVisibleCount(fitting >= rows.length ? undefined : Math.max(1, fitting - 1));
    }, [canCollapse, maxHeight, rows.length]);

    const visibleRows = canCollapse && visibleCount !== undefined ? rows.slice(0, visibleCount) : rows;
    const hiddenCount = rows.length - visibleRows.length;
    const assetMessages = messagesOf('');

    const tagRow = (
        <>
            <hr className={divider} />
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                <span className="tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">{t('DAM.LIBRARY.TAGS')}</span>
                <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
                    {tags.map((tag) => <Badge key={tag} variant="neutral" size="xl">{tag}</Badge>)}
                    <Button variant="ghost" onClick={onManageTags} className="tw-h-auto tw-p-0 tw-font-bold tw-underline">
                        {t('DAM.DETAIL.MANAGE_TAGS')}
                    </Button>
                </div>
            </div>
        </>
    );

    return (
        <section ref={panelRef} className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4" aria-labelledby={`${formId}-title`}>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <h2 id={`${formId}-title`} className="tw-m-0 tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                    {sectionTitle(
                        { name: 'ASSET_DATA', label: layout.sections.find((section) => section.name === 'ASSET_DATA')?.label ?? null },
                        t('DAM.DETAIL.ASSET_DATA'),
                    )}
                </h2>
                {isEditing ? (
                    <div className="tw-flex tw-gap-2">
                        <Button
                            variant="outline"
                            disabled={isSaving}
                            onClick={() => (hasUnsavedChanges ? setIsConfirmingCancel(true) : stopEditing())}
                        >
                            {t('CANCEL')}
                        </Button>
                        <Button type="submit" form={formId} loading={isSaving} disabled={isSaving}>{t('SAVE')}</Button>
                    </div>
                ) : (
                    <Button variant="outline" hasIcon icon={PencilIcon} onClick={() => setIsEditing(true)}>{t('DAM.DETAIL.EDIT')}</Button>
                )}
            </div>
            {assetMessages.length > 0 && (
                <Callout variant="warning" canBeClosed={false}>
                    <ul className="tw-m-0 tw-list-none tw-p-0 tw-text-t-sm lg:tw-text-t-base">
                        {assetMessages.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                </Callout>
            )}
            {isEditing ? (
                <ResolvedLayoutProvider layout={layout}>
                    <FormResetProvider>
                        <DamAssetDataEditor
                            formId={formId}
                            blocks={[
                                { key: 'ASSET_DATA', attributes, rows: editorRows },
                                ...groups.map((group, index) => ({
                                    key: group.name,
                                    title: sectionTitle(group),
                                    attributes: group.attributes,
                                    rows: editorGroupRows[index],
                                })),
                            ]}
                            asset={asset}
                            initialAttributeStates={toAttributeStateLists(states)}
                            initialValidations={toInitialValidations(asset, messages)}
                            onChangesChange={setHasEditorChanges}
                            onSave={async (changed) => {
                                setIsSaving(true);
                                setHasSaveError(false);
                                try {
                                    if (Object.keys(changed).length > 0) {
                                        await onSave(changed);
                                    }
                                    stopEditing();
                                } catch {
                                    setHasSaveError(true);
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                        />
                    </FormResetProvider>
                </ResolvedLayoutProvider>
            ) : (
                <>
                    <div ref={listRef} className="tw-flex tw-flex-col">
                        {visibleRows.map((row) => (
                            <DamDataRow
                                key={row.name}
                                label={row.label}
                                value={row.value}
                                messages={messagesOf(row.name)}
                            />
                        ))}
                    </div>
                    {(hiddenCount > 0 || isExpanded) && (
                        <Button variant="ghost" className="tw-self-center tw-underline" onClick={() => setIsExpanded((expanded) => !expanded)}>
                            {isExpanded ? t('DAM.DETAIL.SHOW_LESS') : t('DAM.DETAIL.SHOW_MORE', { count: hiddenCount.toLocaleString() })}
                        </Button>
                    )}
                    {shownGroups.length > 0 && (
                        <div ref={groupsRef}>
                            <Accordion type="multiple" value={openGroupNames} onValueChange={setOpenGroups}>
                                {shownGroups.map((group, index) => {
                                    const messageCount = group.attributes.reduce((sum, attribute) => sum + messagesOf(attribute.name).length, 0);
                                    const isOpen = openGroupNames.includes(group.name);
                                    return (
                                        <AccordionItem
                                            key={group.name}
                                            value={group.name}
                                            className="tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-divider"
                                        >
                                            <AccordionTrigger className={groupTriggerClassName}>
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    {sectionTitle(group)}
                                                    {!isOpen && messageCount > 0 && (
                                                        <span
                                                            className="tw-flex tw-items-center tw-gap-1 tw-font-normal tw-text-warning-dark"
                                                            aria-label={t('DAM.DETAIL.SECTION_MESSAGES', { count: messageCount.toLocaleString() })}
                                                        >
                                                            <Icon icon={ExclamationTriangleIcon} size="sm" variant="warning" />
                                                            {messageCount.toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </AccordionTrigger>
                                            <AccordionContent>
                                                <div className="tw-flex tw-flex-col">
                                                    {groupRows[index].map((row) => (
                                                        <DamDataRow key={row.name} label={row.label} value={row.value} messages={messagesOf(row.name)} />
                                                    ))}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    );
                                })}
                            </Accordion>
                        </div>
                    )}
                </>
            )}
            {hasSaveError && (
                <Callout variant="destructive" canBeClosed={false}>
                    <span className="tw-text-t-sm lg:tw-text-t-base">{t('DAM.DETAIL.SAVE_FAILED')}</span>
                </Callout>
            )}
            {tagRow}
            <DiscardChangesConfirmationModal show={isConfirmingCancel} onDiscard={stopEditing} onClose={() => setIsConfirmingCancel(false)} />
        </section>
    );
}

const DamDataRow = ({ label, value, messages = [] }: { label: string; value: ReactNode; messages?: string[] }) => (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1 tw-py-2">
        <LabelValue variant="full-width" label={`${label}:`} value={value} />
        {messages.map((message) => (
            <span key={message} className="tw-self-end tw-text-t-xs tw-text-warning-dark lg:tw-text-t-sm">{message}</span>
        ))}
    </div>
);

/** Until A16 (LAX-10533) tells which actions the user may perform, assets count as editable; a 403 on save is reported. */
const canEditAsset = () => true;

/** One block of the editor: the primary attributes or a further section with its title. */
interface DamEditorBlock {
    key: string;
    title?: string;
    attributes: DataModelAttribute[];
    rows: DamDetailRows;
}

interface DamAssetDataEditorProps {
    formId: string;
    blocks: DamEditorBlock[];
    asset: AssetApiRecord;
    initialAttributeStates: Record<string, string[]>;
    initialValidations: Record<string, BaseValidation[]>;
    onChangesChange: (hasChanges: boolean) => void;
    onSave: (changed: Record<string, unknown>) => void;
}

const DamLiveValidation = (props: UseDamLiveValidationOptions) => {
    useDamLiveValidation(props);
    return null;
};

/**
 * Edit mode: every business attribute of all sections through the data-model editor in one
 * form, read-only and hidden states from validation and configuration as in the item editor,
 * validated live (D-051). Sections are open, so no message is hidden (D-056).
 */
const DamAssetDataEditor = ({
    formId,
    blocks,
    asset,
    initialAttributeStates,
    initialValidations,
    onChangesChange,
    onSave,
}: DamAssetDataEditorProps) => {
    const editable = useMemo(
        () => blocks.flatMap((block) => block.attributes).filter((attribute) => !isDamSystemAttribute(attribute.name)),
        [blocks],
    );
    const editableNames = useMemo(() => editable.map(({ name }) => name), [editable]);
    const [startState] = useState(() => ({ initialAttributeStates, initialValidations }));
    const values = useMemo(() => Object.fromEntries(editable.map(({ name }) => [name, asset[name]])), [editable, asset]);
    const form = useForm<FieldValues>({ defaultValues: values as FieldValues });
    const current = useWatch({ control: form.control });

    const changedEntries = useCallback((submitted: FieldValues) => editable
        .filter(({ name }) => !isEqual(normalizeBlank(submitted[name]), normalizeBlank(values[name])))
        .map(({ name }) => [name, submitted[name]] as const), [editable, values]);

    const hasChanges = changedEntries(current).length > 0;
    useEffect(() => {
        onChangesChange(hasChanges);
    }, [hasChanges, onChangesChange]);

    const handleSubmit = form.handleSubmit((submitted) => {
        onSave(Object.fromEntries(changedEntries(submitted)));
    });

    return (
        <FormProvider {...form}>
            <DamLiveValidation
                asset={asset}
                attributeNames={editableNames}
                initialValidations={startState.initialValidations}
                initialAttributeStates={startState.initialAttributeStates}
            />
            <form id={formId} className="tw-flex tw-flex-col tw-gap-2" onSubmit={handleSubmit}>
                <AttributeEditPermissionProvider canEdit={canEditAsset}>
                    {blocks.map((block) => (
                        <div key={block.key} className="tw-flex tw-flex-col tw-gap-2">
                            {block.title && (
                                <>
                                    <hr className={divider} />
                                    <h3 className="tw-m-0 tw-py-1 tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">{block.title}</h3>
                                </>
                            )}
                            {block.attributes.map((attribute) => {
                                if (editableNames.includes(attribute.name)) {
                                    return <AttributeComponent key={attribute.name} attribute={attribute} direction="vertical" disableUrlSync />;
                                }
                                const row = block.rows.find(({ name }) => name === attribute.name);
                                return row ? <DamDataRow key={row.name} label={row.label} value={row.value} /> : null;
                            })}
                        </div>
                    ))}
                </AttributeEditPermissionProvider>
            </form>
        </FormProvider>
    );
};

export { DamAssetDataPanel };
export type { DamAssetDataPanelProps };
